import PlanSalesService from "@/api/services/planSales";
import { toCamelCase, isPurchasedWithin30Days, isVirtualProduct } from "@estaly/ui";
import { sendIntercomMessage } from "@/utils/intercom";

const normalizeStatus = (planSale) => String(planSale?.status ?? "").toLowerCase();

const initialState = {
  index: [],
  currentPage: 1,
  totalPages: 0,
  totalCount: 0,
};

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    setIndex(state, data) {
      state.index = data.records;
      state.currentPage = data.page.currentPage || 1;
      state.totalPages = data.page.totalPages || 0;
      state.totalCount = data.page.totalCount || 0;
    },
  },

  getters: {
    statuses: () => () => {
      return ["active", "expired", "cancelled", "suspended"];
    },

    isPurchasedWithin30Days: () => isPurchasedWithin30Days,

    isActive: () => (planSale) => {
      return normalizeStatus(planSale) === "active";
    },
    isCancelled: () => (planSale) => {
      return normalizeStatus(planSale) === "cancelled";
    },
    isExpired: () => (planSale) => {
      return normalizeStatus(planSale) === "expired";
    },
    isSuspended: () => (planSale) => {
      return normalizeStatus(planSale) === "suspended";
    },
    statusBadgeClass: () => (planSale) => {
      switch (normalizeStatus(planSale)) {
        case "active":
          return "bg-green-200 text-green-800";
        case "expired":
          return "bg-gray-200 text-gray-600";
        case "suspended":
          return "bg-gray-200 text-gray-600";
        case "cancelled":
          return "bg-red-200 text-red-800";
        default:
          return "bg-gray-100 text-gray-500";
      }
    },
    statusListStyle: () => (planSale) => {
      switch (normalizeStatus(planSale)) {
        case "active":
          return "text-green-500 bg-green-100 border border-green-300";
        case "cancelled":
          return "text-red-500 bg-red-100 border border-red-300";
        case "expired":
          return "text-gray-500 bg-gray-100 border border-gray-300";
        case "suspended":
          return "text-yellow-500 bg-yellow-100 border border-yellow-300";
        default:
          return "text-gray-500 bg-gray-100 border border-gray-300";
      }
    },

    hasVirtualProduct: () => (planSale) => {
      return planSale?.products?.some(isVirtualProduct) ?? false;
    },

    productExchangeAllowed: (_state, getters, _rootState, rootGetters) => (planSale, claims) => {
      if (normalizeStatus(planSale) !== "active") return false;
      if (getters.hasVirtualProduct(planSale)) return false;
      if (!Array.isArray(claims)) return false;
      const hasActiveClaim = claims.some((claim) => rootGetters["claims/isActiveClaim"](claim));

      return !planSale?.composedProductType && planSale?.products?.length === 1 && !hasActiveClaim;
    },
  },
  actions: {
    async fetchIndex(
      { commit },
      {
        storeIds = [],
        organizationIds = [],
        monthly = null,
        chargedBy = [],
        status = [],
        keywords = null,
        startDate = null,
        endDate = null,
        purchasedAtStartDate = null,
        purchasedAtEndDate = null,
        page,
        perPage,
        includes = [],
      } = {},
    ) {
      const response = await PlanSalesService.fetchIndex({
        storeIds,
        organizationIds,
        monthly,
        chargedBy,
        status,
        keywords,
        startDate,
        endDate,
        purchasedAtStartDate,
        purchasedAtEndDate,
        page,
        perPage,
        includes,
        showRenewals: false,
      });
      if (!response?.success) return response;

      const data = toCamelCase(response.data);
      commit("setIndex", data);
      return data;
    },

    async fetch(_, { id, includes = [] }) {
      const response = await PlanSalesService.fetch(id, includes);
      if (!response?.success) return response;

      return toCamelCase(response.data);
    },

    async getBillingInvoice(_, { planSaleBillingId }) {
      const response = await PlanSalesService.fetchBillingInvoice(planSaleBillingId);
      if (!response?.success) return response;

      return { ...response, data: toCamelCase(response.data) };
    },

    async getPlanSaleCSVExport(_, filterParams) {
      return await PlanSalesService.fetchCSVExport(filterParams);
    },

    async declareProductExchange(_, { friendlyId, referenceId, title, price, purchaseDate, planSalePurchaseDate }) {
      if (isPurchasedWithin30Days(planSalePurchaseDate)) {
        return await PlanSalesService.exchangeProduct(friendlyId, {
          referenceId,
          title,
          price,
          purchaseDate,
        });
      }

      const message = `Bonjour,

J'espère que vous allez bien. Je me permets de vous contacter concernant une demande d'échange de produit.

Voici les détails du produit concerné :
- Contrat : ${friendlyId}
- Numéro de série / IMEI : ${referenceId}
- Nom du produit : ${title}
- Prix : ${price}€
- Date d'achat : ${purchaseDate}

Serait-il possible de procéder à l'échange de ce produit ?`;

      sendIntercomMessage(message);

      return { success: true, message: "Message envoyé via Intercom" };
    },
  },
};
